import { useState, useMemo, useCallback } from "react";
import { useLayout } from "@/hooks/use-layout";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LayoutDashboard,
  Shield,
  Briefcase,
  Building2,
  Crown,
  AlertTriangle,
  Clock,
  CheckCircle2,
  ChevronRight,
  Loader2,
  ArrowRight,
  FileSpreadsheet,
  Download,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useErrorDialog } from "@/hooks/use-error-dialog";
import { useAuth } from "@/hooks/use-auth";
import type {
  ActionItemsListResponse,
  ActionItemRecord,
  LanesResponse,
  AuthorityLaneRecord,
} from "@shared/v2/types";
import { V2_STATUS_LABELS } from "@shared/v2/constants";
import type { V2ActionItemStatus } from "@shared/v2/constants";

const CLOSED_STATUSES = ["CLOSED", "SENT_UPWARD", "CANCELLED"];

function isOverdue(dueDateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr + "T00:00:00");
  return due < today;
}

function durationLabel(sinceStr: string): string {
  const since = new Date(sinceStr);
  const now = new Date();
  const days = Math.floor((now.getTime() - since.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

const LANE_ICONS: Record<string, typeof Crown> = {
  EDGE_CHAIRMAN: Crown,
  EDGE_MD_OFFICE: Briefcase,
  BOARD_EXCOM: Building2,
  SDF_MD: Shield,
};

const LANE_COLORS: Record<string, { accent: string; bg: string; border: string }> = {
  EDGE_CHAIRMAN: {
    accent: "#00CFE8",
    bg: "rgba(0,207,232,0.08)",
    border: "rgba(0,207,232,0.25)",
  },
  EDGE_MD_OFFICE: {
    accent: "#FF9F43",
    bg: "rgba(255,159,67,0.08)",
    border: "rgba(255,159,67,0.25)",
  },
  BOARD_EXCOM: {
    accent: "#7367F0",
    bg: "rgba(115,103,240,0.08)",
    border: "rgba(115,103,240,0.25)",
  },
  SDF_MD: {
    accent: "#28C76F",
    bg: "rgba(40,199,111,0.08)",
    border: "rgba(40,199,111,0.25)",
  },
};

interface LaneSummary {
  lane: AuthorityLaneRecord;
  total: number;
  open: number;
  overdue: number;
  closed: number;
  byStatus: Record<string, number>;
  items: ActionItemRecord[];
}

function statusLabel(status: string): string {
  return V2_STATUS_LABELS[status as V2ActionItemStatus] || status.replace(/_/g, " ");
}

function priorityColor(priority: string): string {
  switch (priority) {
    case "P1": return "text-red-400";
    case "P2": return "text-orange-400";
    case "P3": return "text-blue-400";
    case "P4": return "text-white/50";
    default: return "text-white/50";
  }
}

export default function V2SummaryPage() {
  const { containerClass } = useLayout();
  const [expandedLane, setExpandedLane] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLane, setReportLane] = useState("ALL");
  const [reportPerson, setReportPerson] = useState("ALL");
  const [reportDepartment, setReportDepartment] = useState("ALL");
  const [reportDateFrom, setReportDateFrom] = useState("");
  const [reportDateTo, setReportDateTo] = useState("");
  const [reportIncludeCompleted, setReportIncludeCompleted] = useState(true);
  const [reportIncludeEscalations, setReportIncludeEscalations] = useState(true);
  const [reportIncludeDeleted, setReportIncludeDeleted] = useState(false);
  const [reportDownloading, setReportDownloading] = useState(false);
  const { toast } = useToast();
  const { showFromApiError, showPermissionError } = useErrorDialog();
  const { isAdmin } = useAuth();

  const itemsQuery = useQuery<ActionItemsListResponse>({
    queryKey: ["/api/action-items"],
  });

  const lanesQuery = useQuery<LanesResponse>({
    queryKey: ["/api/governance/lanes"],
  });

  interface SubSummary { total: number; completed: number; pending: number; overdue: number; subs: { id: string; assigned_to: string; status: string }[] }
  const subSummariesQuery = useQuery<{ data: Record<string, SubSummary> }>({
    queryKey: ["/api/sub-assignments/summaries"],
    queryFn: async () => {
      const res = await fetch("/api/sub-assignments/summaries", { credentials: "include" });
      if (!res.ok) return { data: {} };
      return res.json();
    },
  });
  const subSummaries = subSummariesQuery.data?.data ?? {};

  const peopleQuery = useQuery<{ team_members: { name: string }[] }>({
    queryKey: ["/api/people"],
  });

  const reportDeptsQuery = useQuery<{ data: { id: string; name: string; is_archived: boolean }[] }>({
    queryKey: ["/api/admin/departments"],
    enabled: isAdmin,
  });
  const reportActiveDepts = (reportDeptsQuery.data?.data ?? []).filter((d) => !d.is_archived);

  interface UpwardDispatchKpi {
    targetWorkingDays: number;
    awaitingCount: number;
    overdueCount: number;
    avgLagWorkingDays: number;
    lookbackDays: number;
    byPerson: { name: string; dispatchedCount: number; avgLag: number; overdueCount: number }[];
  }
  const dispatchKpiQuery = useQuery<{ data: any[]; upwardDispatch?: UpwardDispatchKpi }>({
    queryKey: ["/api/admin/kpi", ""],
    queryFn: async () => {
      const res = await fetch("/api/admin/kpi", { credentials: "include" });
      if (!res.ok) return { data: [] };
      return res.json();
    },
    enabled: isAdmin,
  });
  const upwardDispatch = dispatchKpiQuery.data?.upwardDispatch;

  const handleDownloadReport = useCallback(async () => {
    setReportDownloading(true);
    try {
      const params = new URLSearchParams();
      if (reportLane !== "ALL") params.set("lane", reportLane);
      if (reportPerson !== "ALL") params.set("person", reportPerson);
      if (reportDepartment !== "ALL") params.set("department", reportDepartment);
      if (reportDateFrom) params.set("dateFrom", reportDateFrom);
      if (reportDateTo) params.set("dateTo", reportDateTo);
      params.set("includeCompleted", String(reportIncludeCompleted));
      params.set("includeEscalations", String(reportIncludeEscalations));
      params.set("includeDeleted", String(reportIncludeDeleted));
      try { params.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone); } catch {}

      const response = await fetch(`/api/report/export?${params.toString()}`, {
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 403) {
          showPermissionError("Generating reports is restricted to administrators. Please ask an HR Admin to generate the report for you.");
          return;
        }
        let serverMsg = "";
        try {
          const body = await response.json();
          serverMsg = body?.message || "";
        } catch {}
        throw new Error(`${response.status}: ${serverMsg || "Failed to generate report"}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disposition = response.headers.get("Content-Disposition");
      const match = disposition?.match(/filename="(.+)"/);
      a.download = match?.[1] || `governance-report-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: "Report downloaded" });
      setReportOpen(false);
    } catch (err: any) {
      showFromApiError("Download Failed", err);
    } finally {
      setReportDownloading(false);
    }
  }, [reportLane, reportPerson, reportDepartment, reportDateFrom, reportDateTo, reportIncludeCompleted, reportIncludeEscalations, reportIncludeDeleted, toast]);

  const laneSummaries = useMemo<LaneSummary[]>(() => {
    if (!itemsQuery.data?.data || !lanesQuery.data?.data) return [];
    const items = itemsQuery.data.data;
    const lanes = [...lanesQuery.data.data].sort((a, b) => a.sort_order - b.sort_order);

    return lanes.map((lane) => {
      const laneItems = items.filter((i) => i.authority_lane === lane.lane_key);
      const open = laneItems.filter((i) => !CLOSED_STATUSES.includes(i.status));
      const overdue = open.filter((i) => i.due_date && isOverdue(i.due_date));
      const closed = laneItems.filter((i) => CLOSED_STATUSES.includes(i.status));
      const byStatus: Record<string, number> = {};
      for (const item of laneItems) {
        byStatus[item.status] = (byStatus[item.status] || 0) + 1;
      }
      return {
        lane,
        total: laneItems.length,
        open: open.length,
        overdue: overdue.length,
        closed: closed.length,
        byStatus,
        items: laneItems,
      };
    });
  }, [itemsQuery.data, lanesQuery.data]);

  const globalStats = useMemo(() => {
    const items = itemsQuery.data?.data || [];
    const open = items.filter((i) => !CLOSED_STATUSES.includes(i.status));
    const overdue = open.filter((i) => i.due_date && isOverdue(i.due_date));
    return {
      total: items.length,
      open: open.length,
      overdue: overdue.length,
      closed: items.length - open.length,
    };
  }, [itemsQuery.data]);

  const isLoading = itemsQuery.isLoading || lanesQuery.isLoading;

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)" }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-[#00CFE8]" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)" }}
    >
      <div className={`${containerClass} mx-auto px-6 py-8`}>
        <div className="mb-8">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="h-6 w-6 text-[#00CFE8]" />
              <h1 className="text-2xl font-bold text-white" data-testid="text-page-title">
                V2 Governance Summary
              </h1>
            </div>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                className="bg-white/5 border-white/15 text-white hover:bg-white/10"
                onClick={() => setReportOpen(true)}
                data-testid="button-generate-report"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
            )}
          </div>
          <p className="text-white/50 text-sm">
            Action items across all authority lanes. Click a lane to drill down.
          </p>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-4 flex flex-col items-center gap-1">
              <p className="text-3xl font-bold text-white" data-testid="text-stat-total">
                {globalStats.total}
              </p>
              <p className="text-white/50 text-xs">Total Items</p>
            </CardContent>
          </Card>
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-4 flex flex-col items-center gap-1">
              <p className="text-3xl font-bold text-[#00CFE8]" data-testid="text-stat-open">
                {globalStats.open}
              </p>
              <p className="text-white/50 text-xs">Open</p>
            </CardContent>
          </Card>
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-4 flex flex-col items-center gap-1">
              <p className="text-3xl font-bold text-red-400" data-testid="text-stat-overdue">
                {globalStats.overdue}
              </p>
              <p className="text-white/50 text-xs">Overdue</p>
            </CardContent>
          </Card>
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-4 flex flex-col items-center gap-1">
              <p className="text-3xl font-bold text-[#28C76F]" data-testid="text-stat-closed">
                {globalStats.closed}
              </p>
              <p className="text-white/50 text-xs">Closed</p>
            </CardContent>
          </Card>
        </div>

        {isAdmin && upwardDispatch && (
          <Card
            className="border-white/10 mb-8"
            style={{ background: "rgba(255,255,255,0.04)" }}
            data-testid="card-upward-dispatch"
          >
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-white">Upward Dispatch Performance</h3>
                  <p className="text-white/50 text-xs">
                    Above-MD items the SDF MD has approved. Target: dispatched within{" "}
                    <strong className="text-white/80">{upwardDispatch.targetWorkingDays}</strong> working day(s).
                    Last {upwardDispatch.lookbackDays} days.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-md p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
                  <p className="text-2xl font-bold text-white" data-testid="text-dispatch-awaiting">
                    {upwardDispatch.awaitingCount}
                  </p>
                  <p className="text-white/50 text-xs">Awaiting dispatch</p>
                </div>
                <div className="rounded-md p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
                  <p
                    className={`text-2xl font-bold ${upwardDispatch.overdueCount > 0 ? "text-red-400" : "text-[#28C76F]"}`}
                    data-testid="text-dispatch-overdue"
                  >
                    {upwardDispatch.overdueCount}
                  </p>
                  <p className="text-white/50 text-xs">Past target</p>
                </div>
                <div className="rounded-md p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
                  <p className="text-2xl font-bold text-[#00CFE8]" data-testid="text-dispatch-avg-lag">
                    {upwardDispatch.avgLagWorkingDays}
                  </p>
                  <p className="text-white/50 text-xs">Avg lag (working days)</p>
                </div>
              </div>
              {upwardDispatch.byPerson.length > 0 ? (
                <div className="overflow-x-auto rounded-md border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 text-white/60">
                      <tr>
                        <th className="p-2 text-left">Dispatcher</th>
                        <th className="p-2 text-left">Dispatched</th>
                        <th className="p-2 text-left">Avg lag (days)</th>
                        <th className="p-2 text-left">Late</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upwardDispatch.byPerson.map((p) => {
                        const target = upwardDispatch.targetWorkingDays;
                        const chip =
                          p.avgLag <= target
                            ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                            : p.avgLag <= target + 2
                            ? "bg-amber-500/20 text-amber-300 border-amber-500/30"
                            : "bg-red-500/20 text-red-300 border-red-500/30";
                        return (
                          <tr key={p.name} className="border-t border-white/5" data-testid={`row-dispatcher-${p.name}`}>
                            <td className="p-2 text-white/80">{p.name}</td>
                            <td className="p-2 text-white/80">{p.dispatchedCount}</td>
                            <td className="p-2">
                              <Badge variant="outline" className={chip}>
                                {p.avgLag}
                              </Badge>
                            </td>
                            <td className="p-2 text-white/80">
                              {p.overdueCount > 0 ? (
                                <span className="text-red-400">{p.overdueCount}</span>
                              ) : (
                                <span className="text-white/40">0</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-white/50 text-xs italic">No dispatches recorded in the last {upwardDispatch.lookbackDays} days.</div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {laneSummaries.map((ls) => {
            const colors = LANE_COLORS[ls.lane.lane_key] || LANE_COLORS.EDGE_CHAIRMAN;
            const LaneIcon = LANE_ICONS[ls.lane.lane_key] || Shield;
            const isExpanded = expandedLane === ls.lane.lane_key;

            return (
              <div key={ls.lane.id} data-testid={`lane-card-${ls.lane.lane_key}`}>
                <Card
                  className="border cursor-pointer transition-colors"
                  style={{
                    background: colors.bg,
                    borderColor: isExpanded ? colors.accent : colors.border,
                  }}
                  onClick={() =>
                    setExpandedLane(isExpanded ? null : ls.lane.lane_key)
                  }
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-md flex items-center justify-center"
                          style={{ background: `${colors.accent}20` }}
                        >
                          <LaneIcon
                            className="h-5 w-5"
                            style={{ color: colors.accent }}
                          />
                        </div>
                        <div>
                          <h2
                            className="text-lg font-semibold text-white"
                            data-testid={`text-lane-name-${ls.lane.lane_key}`}
                          >
                            {ls.lane.display_name}
                          </h2>
                          <p className="text-white/40 text-xs font-mono">
                            {ls.lane.lane_key}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="text-center min-w-[50px]">
                          <p
                            className="text-xl font-bold text-white"
                            data-testid={`text-lane-total-${ls.lane.lane_key}`}
                          >
                            {ls.total}
                          </p>
                          <p className="text-white/40 text-xs">TOTAL</p>
                        </div>
                        <div className="text-center min-w-[50px]">
                          <p
                            className="text-xl font-bold"
                            style={{ color: colors.accent }}
                            data-testid={`text-lane-open-${ls.lane.lane_key}`}
                          >
                            {ls.open}
                          </p>
                          <p className="text-white/40 text-xs">OPEN</p>
                        </div>
                        <div className="text-center min-w-[50px]">
                          <p
                            className={`text-xl font-bold ${ls.overdue > 0 ? "text-red-400" : "text-white/30"}`}
                            data-testid={`text-lane-overdue-${ls.lane.lane_key}`}
                          >
                            {ls.overdue}
                          </p>
                          <p className={`text-xs ${ls.overdue > 0 ? "text-red-400/60" : "text-white/40"}`}>OVERDUE</p>
                        </div>
                        <div className="text-center min-w-[50px]">
                          <p
                            className="text-xl font-bold text-[#28C76F]"
                            data-testid={`text-lane-closed-${ls.lane.lane_key}`}
                          >
                            {ls.closed}
                          </p>
                          <p className="text-white/40 text-xs">CLOSED</p>
                        </div>

                        <ChevronRight
                          className={`h-5 w-5 text-white/40 transition-transform ${
                            isExpanded ? "rotate-90" : ""
                          }`}
                        />
                      </div>
                    </div>

                    {Object.keys(ls.byStatus).length > 0 && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {Object.entries(ls.byStatus)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([status, count]) => (
                            <Badge
                              key={status}
                              variant="outline"
                              className="text-xs border-white/15 text-white/60"
                            >
                              {statusLabel(status)}: {count}
                            </Badge>
                          ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {isExpanded && ls.items.length > 0 && (
                  <div className="mt-2 ml-6 space-y-1">
                    {ls.items.map((item) => {
                      const overdue =
                        !CLOSED_STATUSES.includes(item.status) &&
                        item.due_date &&
                        isOverdue(item.due_date);
                      return (
                        <Link
                          key={item.id}
                          href={`/action-items/${item.id}`}
                          className="block"
                          data-testid={`link-item-${item.id}`}
                        >
                          <Card
                            className="border-white/8 transition-colors hover-elevate"
                            style={{
                              background: overdue
                                ? "rgba(255,50,50,0.06)"
                                : "rgba(255,255,255,0.04)",
                              borderColor: overdue
                                ? "rgba(255,50,50,0.2)"
                                : undefined,
                            }}
                          >
                            <CardContent className="p-3 flex items-center justify-between gap-4 flex-wrap">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="min-w-0 flex-1">
                                  {item.meeting_name && (
                                    <div
                                      className="text-xs text-white/70 truncate mb-0.5"
                                      data-testid={`text-item-opportunity-${item.id}`}
                                    >
                                      {item.meeting_name}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span
                                      className="text-sm font-medium text-white truncate"
                                      data-testid={`text-item-title-${item.id}`}
                                    >
                                      {item.title}
                                    </span>
                                    <Badge
                                      variant="outline"
                                      className={`text-xs ${priorityColor(item.priority)} border-current`}
                                    >
                                      {item.priority}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-3 mt-0.5 text-xs text-white/40 flex-wrap">
                                    <span>{item.assigned_to}</span>
                                    {(() => {
                                      const pendingSubs = subSummaries[item.id]?.subs.filter(s => s.status !== "COMPLETED") ?? [];
                                      if (pendingSubs.length > 0) {
                                        const uniqueNames = [...new Set(pendingSubs.map(s => s.assigned_to))];
                                        return (
                                          <span className="text-[#2563eb] font-medium">
                                            With: {uniqueNames.join(", ")}
                                            <span className="text-[#2563eb]/60 font-normal"> (sub-tasks)</span>
                                          </span>
                                        );
                                      }
                                      if (item.current_responsible) {
                                        return (
                                          <span className="text-[#FF9F43] font-medium">
                                            With: {item.current_responsible}
                                            {item.responsible_since && (
                                              <span className="text-[#FF9F43]/70 font-normal"> — {durationLabel(item.responsible_since)}</span>
                                            )}
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                    {item.due_date && (
                                      <span className={overdue ? "text-red-400" : ""}>
                                        {overdue && (
                                          <AlertTriangle className="h-3 w-3 inline mr-1" />
                                        )}
                                        Due {new Date(item.due_date + "T00:00:00").toLocaleDateString("en-GB", {day:"2-digit",month:"short",year:"numeric"})}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className="text-xs border-white/15 text-white/60"
                                  data-testid={`text-item-status-${item.id}`}
                                >
                                  {statusLabel(item.status)}
                                </Badge>
                                <ArrowRight className="h-4 w-4 text-white/30" />
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {laneSummaries.length === 0 && !isLoading && (
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-8 flex flex-col items-center gap-3">
              <LayoutDashboard className="h-10 w-10 text-white/30" />
              <p className="text-white/50" data-testid="text-empty-state">
                No governance lanes configured.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-[#1a1a3e] border-white/15 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <FileSpreadsheet className="h-5 w-5 text-[#00CFE8]" />
              Generate Report
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Authority Lane</Label>
              <Select value={reportLane} onValueChange={setReportLane}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-report-lane">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a3e] border-white/15">
                  <SelectItem value="ALL" className="text-white">All Lanes</SelectItem>
                  {(lanesQuery.data?.data || []).map(l => (
                    <SelectItem key={l.lane_key} value={l.lane_key} className="text-white">
                      {l.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Person (KPI evidence)</Label>
              <Select value={reportPerson} onValueChange={setReportPerson}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-report-person">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a3e] border-white/15">
                  <SelectItem value="ALL" className="text-white">All People</SelectItem>
                  {(peopleQuery.data?.team_members || []).map(p => (
                    <SelectItem key={p.name} value={p.name} className="text-white">
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-white/60 text-xs">From Date</Label>
                <Input
                  type="date"
                  value={reportDateFrom}
                  onChange={(e) => setReportDateFrom(e.target.value)}
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid="input-report-date-from"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/60 text-xs">To Date</Label>
                <Input
                  type="date"
                  value={reportDateTo}
                  onChange={(e) => setReportDateTo(e.target.value)}
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid="input-report-date-to"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Department</Label>
              <Select value={reportDepartment} onValueChange={setReportDepartment}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-report-department">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a3e] border-white/15">
                  <SelectItem value="ALL" className="text-white">All Departments</SelectItem>
                  {reportActiveDepts.map((d) => (
                    <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="includeCompleted"
                checked={reportIncludeCompleted}
                onCheckedChange={(c) => setReportIncludeCompleted(!!c)}
                className="border-white/30 data-[state=checked]:bg-[#28C76F] data-[state=checked]:border-[#28C76F]"
                data-testid="checkbox-include-completed"
              />
              <Label htmlFor="includeCompleted" className="text-white/70 text-sm cursor-pointer">
                Include completed items (separate sheet)
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="includeEscalations"
                checked={reportIncludeEscalations}
                onCheckedChange={(c) => setReportIncludeEscalations(!!c)}
                className="border-white/30 data-[state=checked]:bg-[#FF9F43] data-[state=checked]:border-[#FF9F43]"
                data-testid="checkbox-include-escalations"
              />
              <Label htmlFor="includeEscalations" className="text-white/70 text-sm cursor-pointer">
                Include escalations (separate sheet)
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="includeDeleted"
                checked={reportIncludeDeleted}
                onCheckedChange={(c) => setReportIncludeDeleted(!!c)}
                className="border-white/30 data-[state=checked]:bg-[#EA5455] data-[state=checked]:border-[#EA5455]"
                data-testid="checkbox-include-deleted"
              />
              <Label htmlFor="includeDeleted" className="text-white/70 text-sm cursor-pointer">
                Include soft-deleted items (separate sheet)
              </Label>
            </div>

            <p className="text-white/30 text-xs">
              Downloads an Excel file with active items plus optional sheets for completed items, escalations, and soft-deleted items within the selected date range.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="bg-white/5 border-white/15 text-white hover:bg-white/10"
              onClick={() => setReportOpen(false)}
              data-testid="button-cancel-report"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-[#00CFE8] hover:bg-[#00CFE8]/80 text-black font-medium"
              onClick={handleDownloadReport}
              disabled={reportDownloading}
              data-testid="button-download-report"
            >
              {reportDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
